import type { RepositoryIntelligenceSymbolReference } from '@ashniva/types';
import { Button, SkeletonText } from '@ashniva/ui';

import { useRiSymbolQuery } from '../api-code';
import { splitLines } from '../code-window';
import { linesLabel } from '../ri-text';
import { useWorkspace } from '../workspace-context';
import { CodeViewer } from './CodeViewer';
import { ProblemNotice } from './ProblemNotice';

/** One symbol: its signature and code, its callers and callees, its tests and its file's imports (P14-05). */
export function SymbolDetail({ symbolId }: { symbolId: string }) {
  const { linkId, openCode } = useWorkspace();
  const query = useRiSymbolQuery(linkId, symbolId);
  if (query.isLoading) {
    return (
      <div aria-busy="true">
        <span className="sr-only">Loading the symbol</span>
        <SkeletonText lines={8} />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return <ProblemNotice error={query.error} onRetry={() => void query.refetch()} size="sm" />;
  }
  const { symbol, code, references, dependencies, tests } = query.data;
  return (
    <div className="ri-stack">
      <h3 className="ri-mono">{symbol.qualifiedName || symbol.name}</h3>
      <div className="ri-row ri-muted">
        <span>{symbol.kind}</span>
        <span className="ri-mono">{symbol.path}</span>
        <span>{linesLabel(symbol.startLine, symbol.endLine)}</span>
        <Button
          size="sm"
          onClick={() =>
            openCode({ path: symbol.path, start: symbol.startLine, end: symbol.endLine })
          }
        >
          Open in its file
        </Button>
      </div>
      {symbol.signature ? <pre className="ri-snippet">{symbol.signature}</pre> : null}
      <CodeViewer
        label={symbol.qualifiedName || symbol.name}
        lines={splitLines(code.content)}
        firstLine={code.startLine}
      />
      {code.truncated ? (
        <p className="ri-muted">Only the first part of the code is shown.</p>
      ) : null}
      <References title="Called by" items={references.callers} onOpen={openCode} />
      <References title="Calls" items={references.callees} onOpen={openCode} />
      {references.truncated ? <p className="ri-muted">Not every reference is listed.</p> : null}
      <section aria-label="Tests">
        <h4>Tests</h4>
        {tests.length === 0 ? (
          <p className="ri-muted">No tests of it were found.</p>
        ) : (
          <ul className="ri-list">
            {tests.map((test) => (
              <li key={`${test.path}:${test.name ?? ''}`} className="ri-row">
                <span className="ri-mono">{test.path}</span>
                {test.name ? <span>{test.name}</span> : null}
                <span className="ri-muted">
                  {test.relation === 'direct' ? 'tests it directly' : 'tests it indirectly'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-label="Its file's imports and dependents">
        <h4>Imports and dependents of its file</h4>
        <p className="ri-muted">Imports: {dependencies.imports.join(', ') || 'none'}</p>
        <p className="ri-muted">Used by: {dependencies.dependents.join(', ') || 'none found'}</p>
      </section>
    </div>
  );
}

function References({
  title,
  items,
  onOpen,
}: {
  title: string;
  items: RepositoryIntelligenceSymbolReference[];
  onOpen: (target: { path: string; start: number; end: number }) => void;
}) {
  return (
    <section aria-label={title}>
      <h4>{title}</h4>
      {items.length === 0 ? (
        <p className="ri-muted">None found.</p>
      ) : (
        <ul className="ri-list">
          {items.map((item, index) => (
            <li key={`${item.path}:${item.line}:${index}`} className="ri-row">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onOpen({ path: item.path, start: item.line, end: item.line })}
              >
                {item.path}, line {item.line}
              </Button>
              {item.symbol ? <span className="ri-mono">{item.symbol}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
